import { useSortable } from "@dnd-kit/sortable";

import ActionListItem from "@/appComponents/Blocks/Composites/ActionListItem/ActionListItem";
import { Flex, Icon } from "@theorgchartdev/storybook";
import { RulePairInput } from "@/appComponents/Blocks/Composites/RuleInput/RulePairInput/RulePairInput";
import { QRulePair } from "@coreapp/CoreApp/QRulePair";
import { appstr } from "@coreapp/utils/appstr";
import EditableItemGroup from "@/appComponents/Blocks/Utils/EditableItemGroup/EditableItemGroup";
import { UIRuleError } from "@/appComponents/Blocks/Composites/RuleInput/ruleErrors";

type Props = {
  isEditing: boolean;
  toggleEditStatus?: () => void;
  ruleIndex: number;
  rule: QRulePair;
  handleRuleRemove: () => void;
  insertEmptyRuleNextToThisRule: () => void;
  setRule: (rule: QRulePair) => void;
  criteriaN: string;
  testIdPrefix?: string;
  planningTestIdPrefix?: string;
  errorsObject?: UIRuleError;
};

export const BlockCriteria = (props: Props) => {
  const {
    isEditing,
    toggleEditStatus,
    ruleIndex,
    rule,
    handleRuleRemove,
    setRule,
    insertEmptyRuleNextToThisRule,
    criteriaN,
    testIdPrefix,
    planningTestIdPrefix,
    errorsObject
  } = props;

  const { attributes, listeners, setNodeRef } = useSortable({
    id: `rule-${ruleIndex}`,
    data: { rule, ruleIndex, criteriaN }
  });

  return (
    <div ref={setNodeRef} className="ocn-filter-criteria-card" data-rule-uid={rule.fUID}>
      <Flex direction={"column"}>
        <div onClick={toggleEditStatus} className="oc-filter-criteria-card-header">
          <ActionListItem
            className="oc-filter-criteria-card-header-action-list"
            padding="8px"
            dataTestId={`rule-${ruleIndex}-header`}
            actions={[
              {
                iconName: "plus",
                tooltipProps: {
                  message: appstr("Add entity", { entity: appstr("Criteria").toLowerCase() })
                },
                onClick: () => insertEmptyRuleNextToThisRule(),
                dataTestId: testIdPrefix ? `${testIdPrefix}-add-btn` : undefined
              },
              {
                iconName: "trash-can",
                iconProps: { color: "error" },
                tooltipProps: { message: appstr("Delete criteria") },
                onClick: handleRuleRemove,
                dataTestId: planningTestIdPrefix
                  ? `${planningTestIdPrefix}-delete-btn`
                  : `rule-${ruleIndex}-delete`
              }
            ]}
            label={appstr("Entity i", { entity: appstr("Criteria"), i: criteriaN })}
            visible="always"
            contentBefore={
              <EditableItemGroup
                actions={[
                  {
                    active: true,
                    iconName: "grip-dots-vertical",
                    wrapperProps: { ...attributes, ...listeners }
                  }
                ]}
              />
            }
            contentAfter={
              <Icon
                name={isEditing ? "chevron-down" : "chevron-right"}
                size="xsmall"
                className="ocn-filter-card-chevron"
              />
            }
            innerFlexProps={{ className: "ocn-filter-criteria-card-inner-header", flex: "none" }}
            spaceBetween={false}
          />
        </div>
        {isEditing && (
          <>
            <RulePairInput
              dataTestIdPrefix={planningTestIdPrefix ?? `rule-${ruleIndex}`}
              popoverTestIdPrefix={testIdPrefix}
              allowHierarchyFields={false}
              variant={"compact"}
              direction={"column"}
              editing={true}
              onChange={setRule}
              value={rule}
              showFieldComparisons={true}
              blocksStructure
              requiredMarker
              selectSize="medium"
              fieldError={errorsObject?.fieldError?.[0]}
              valueError={errorsObject?.valueError?.[0]}
            />
            {errorsObject?.generalRuleError?.[0]?.hasError && (
              <span
                className="ocn-criteria-general-error ocn-form-input-message ocn-form-input-message--error"
                role="alert"
              >
                {errorsObject.generalRuleError[0].errorString}
              </span>
            )}
          </>
        )}
      </Flex>
    </div>
  );
};
